import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImg } from "@/components/ProductCard";
import { FdaDisclaimer, ShippingRestriction } from "@/components/Compliance";

const coaImgUrl = (item) => (typeof item === "string" ? item : item?.url);
const coaImgName = (item) => (typeof item === "string" ? "" : item?.name || "");

export default function Coa() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const withCoa = products.filter(
    (p) => (p.coa_images || []).length > 0 || p.coa_batch_number || p.coa_lab_name || p.coa_test_date
  );

  return (
    <div className="mx-auto max-w-6xl px-6 py-12" data-testid="coa-page">
      <div className="mb-3 flex items-center gap-2 text-[#a06d34]">
        <FlaskConical className="h-5 w-5" strokeWidth={1.5} />
        <p className="overline">Lab Tested</p>
      </div>
      <h1 className="mb-3 text-4xl font-black text-[#2b2520] sm:text-5xl">Certificates of Analysis</h1>
      <p className="mb-10 max-w-2xl text-sm text-neutral-600">
        Every product we ship is third-party lab tested. Browse the Certificate of Analysis (COA)
        for each item below, or scan the QR code on any product page.
      </p>

      {loading ? (
        <p className="py-20 text-center text-neutral-500">Loading…</p>
      ) : withCoa.length === 0 ? (
        <p className="py-20 text-center text-neutral-500" data-testid="coa-empty">
          Lab results are being uploaded. Check back shortly.
        </p>
      ) : (
        <div className="space-y-4" data-testid="coa-list">
          {withCoa.map((p) => (
            <div
              key={p.id}
              data-testid={`coa-item-${p.id}`}
              className="flex flex-col gap-5 border border-black/10 bg-white p-5 sm:flex-row sm:items-start"
            >
              <Link to={`/product/${p.id}`} className="shrink-0">
                <img src={resolveImg(p.image_url)} alt={p.name} className="h-24 w-24 object-cover" />
              </Link>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#a06d34]" strokeWidth={1.5} />
                  <Link to={`/product/${p.id}`} className="text-lg font-bold text-[#2b2520] hover:text-[#a06d34]">
                    {p.name}
                  </Link>
                </div>
                <div className="space-y-0.5 text-sm text-neutral-600">
                  {p.coa_batch_number && (
                    <p>Batch #: <span className="font-mono text-[#2b2520]">{p.coa_batch_number}</span></p>
                  )}
                  {p.coa_lab_name && <p>Lab: {p.coa_lab_name}</p>}
                  {p.coa_test_date && <p>Tested: {p.coa_test_date}</p>}
                  {p.category === "flower" && (p.thca || p.d9) ? (
                    <p>Potency: THCa {p.thca}% · D9 {p.d9}%</p>
                  ) : null}
                </div>
                {(p.coa_images || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {p.coa_images.map((item, i) => (
                      <a
                        key={i}
                        href={resolveImg(coaImgUrl(item))}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`coa-doc-${p.id}-${i}`}
                        className="block w-28 border border-black/10 transition-colors hover:border-[#c99a63]"
                      >
                        <img src={resolveImg(coaImgUrl(item))} alt={`COA ${i + 1}`} className="h-28 w-28 object-cover" />
                        {coaImgName(item) && (
                          <p className="truncate px-1 py-1 text-[10px] text-neutral-600" title={coaImgName(item)}>
                            {coaImgName(item)}
                          </p>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 space-y-3 border-t border-black/10 pt-8">
        <ShippingRestriction />
        <FdaDisclaimer />
      </div>
    </div>
  );
}
